import * as lo from "lodash";

function isSmallScreen() {
  // < bootstrap lg
  const BOOTSTRAP_LG_WIDTH = 992;
  return screen.width < BOOTSTRAP_LG_WIDTH;
}

export function verifyInterface(
  controllerCtxt,
  config = {
    requiredInterface: [],
    allValues: false,
    allTargets: false,
    checkCSRF: false,
    throw: true,
  },
) {
  /*
      Verify the Interface of a controller - returns the list of the missingInterface
      Available config options are:
          - requiredInterface
            String[] - default: [] - specific Interface to check (like 'someImportantParamValue' or 'myNiceTarget')
          - allValues
            bool - default: false - add all Values to requiredInterface
          - allTargets
            bool - default: false - add all Targets to requiredInterface
          - checkCSRF
            bool - default: false - check for a CSRF token
          - throw
            bool - default: true - throw an Error if something is missing, if this is false, the missing interface is returned
       */
  for (const key of Object.keys(config)) {
    if (
      ![
        "requiredInterface",
        "allValues",
        "allTargets",
        "checkCSRF",
        "throw",
      ].includes(key)
    ) {
      throw new Error(
        `DEVELOPMENT ERROR - ${key} not a valid config for 'getMissingThings'`,
      );
    }
  }
  const availableInterface = [];
  const keyToHas = (key) => `has${key.charAt(0).toUpperCase()}${key.slice(1)}`;
  const requiredInterface = config.requiredInterface ?? [];
  Object.keys(controllerCtxt.constructor.values).forEach((valName) => {
    const name = `${valName}Value`;
    availableInterface.push(name);
    if (config.allValues) {
      requiredInterface.push(name);
    }
  });
  controllerCtxt.constructor.targets.forEach((valName) => {
    const name = `${valName}Target`;
    availableInterface.push(name);
    if (config.allTargets) {
      requiredInterface.push(name);
    }
  });
  const missingInterface = requiredInterface.filter(
    (key) => !controllerCtxt[keyToHas(key)],
  );
  if (config.checkCSRF) {
    try {
      getHeaderWithCSRF();
    } catch (err) {
      missingInterface.push(err.message);
    }
  }
  let additionalInfo = `${missingInterface.length} misses.`;
  if (config.throw && missingInterface.length > 0) {
    const sortedInterface = lo.orderBy(availableInterface, [
      (i) => i.toLowerCase(),
    ]);
    if (missingInterface.length === 1) {
      const target = missingInterface[0];
      let closestMatch = [];
      let threshold = 0;
      while (threshold < 4 && !closestMatch) {
        threshold += 1;
        closestMatch = findClosestMatch(target, sortedInterface, threshold);
      }
      if (closestMatch.length > 0) {
        additionalInfo = `Failed to find '${target}'. Did you mean ${closestMatch} ?`;
      } else {
        additionalInfo = `No potential typo of '${target}' found. Make sure 
             * the interface is in the 'values' or 'targets' attribute on the controller
             * values are spelled correctly and given in the same html element where the controller is hooked
             * targets are spelled correctly and you can see them in the dom
            `;
      }
    }
    throw new Error(
      `DEVELOPMENT ERROR - on ${controllerCtxt.identifier}:
      
       ${additionalInfo}
       
       Missing Interface: ${missingInterface}.
       
       Available Interface: ${sortedInterface}`,
    );
  }
  return missingInterface;
}

async function hasCam() {
  if (!navigator.mediaDevices) return false;
  if (!navigator.mediaDevices.getUserMedia) return false;
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some((device) => device.kind === "videoinput");
  } catch (e) {
    console.error(e);
    return false;
  }
}

async function getCanUseScanner() {
  const urlSearchParams = new URLSearchParams(window.location.search);
  if (!urlSearchParams.has("can_use_scanner")) {
    return hasCam();
  }
  return urlSearchParams.get("can_use_scanner").toLowerCase() == "true";
}

export async function getCanAndShouldUseScanner() {
  const canUseScanner = await getCanUseScanner();
  const shouldUseScanner = isSmallScreen() && canUseScanner;
  return { canUseScanner, shouldUseScanner };
}

export async function waitMs(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function joinUrl(baseUrl, pathList, paramsMap) {
  const url = new URL(baseUrl.replace(/\/$/, ""), window.location.href);
  for (let path of pathList) {
    path = path.replace(/\/$/, "");
    url.pathname += "/" + path;
  }
  url.pathname += "/";
  if (paramsMap) {
    const searchParams = new URLSearchParams(paramsMap);
    url.search = searchParams.toString();
  }
  return url;
}

export function fetchWithCSRF(input, init) {
  init.headers = { ...(init.headers ?? {}), ...getHeaderWithCSRF() };
  console.debug("calling fetch with ", input, init);
  return fetch(input, init);
}

export function getHeaderWithCSRF() {
  try {
    const csrfMeta = document.head.querySelector("[name=csrf-token]");
    return {
      "X-CSRF-TOKEN": csrfMeta.content,
    };
  } catch {
    // continue to look for {% csrf_token %}
  }
  try {
    const csfrElement = document.querySelector("[name=csrfmiddlewaretoken]");
    return {
      "X-CSRF-TOKEN": csfrElement.value,
    };
  } catch {
    throw new Error(`\
      Failed to identify CSRF token. 
      You are outside the base.html. 
      If this is intentional, add {% csrf_token %} to the template.
      `);
  }
}

/**
 * A utility object for working with cookies.
 */
export const cookie = {
  /**
   * Sets a cookie with the given key, value, and expiration days.
   *
   * @param {string} key - The key for the cookie.
   * @param {string} [val] - The value to store in the cookie.
   * @param {number} [days] - The number of days until the cookie expires (default is 1 day).
   * @throws {Error} Throws an error if the key is not provided.
   */
  set: (key, val, days = 1) => {
    if (!key) {
      throw new Error("invalid use of `cookie.set` - key not given");
    }
    if (!days) {
      // if no value is provided, set the expiration date to the past, effectively deleting it.
      days = val ? 1 : -1;
    }
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${key}=${val}; expires=${d.toUTCString()}; Path=/; SameSite=Strict; Secure`;
  },
  /**
   * Resets a cookie by setting its expiration date to the past, effectively deleting it.
   *
   * @param {string} key - The key of the cookie to reset.
   */
  reset: (key) => cookie.set(key),

  /**
   * Gets the value of a cookie by its key.
   *
   * @param {string} [key] - The key of the cookie to retrieve. If not provided, returns all cookies as a map.
   * @returns {string|Object} The value of the cookie with the given key, or an object containing all cookies.
   */
  get: (key) => {
    const cookieMap = Object.fromEntries(
      decodeURIComponent(document.cookie)
        .split(";")
        .map((keyValPair) => keyValPair.trim().split("=")),
    );
    if (!key) return cookieMap;
    return cookieMap[key];
  },
};

const ESC_MAP = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

export function escapeHTML(s, forAttribute) {
  return s.replace(forAttribute ? /[&<>'"]/g : /[&<>]/g, function (c) {
    return ESC_MAP[c];
  });
}

export async function getTurboResponse(url, body, init) {
  const requestInit = { method: "POST", ...init };
  if (["GET", "HEAD"].includes(requestInit.method)) {
    if (!isEmpty(body))
      throw new TypeError("Request with GET/HEAD method cannot have body.");
  } else {
    requestInit.body = body;
  }

  const response = await fetchWithCSRF(url, requestInit);
  if (!(response.status < 500)) {
    throw response;
  }
  const contentType = response.headers.get("Content-Type") ?? "unset";
  const is_stream = contentType.startsWith("text/vnd.turbo-stream");
  let html = await response.text();
  return { response, is_stream, html };
}

export function isEmpty(obj) {
  if (typeof obj !== "object") {
    throw new Error(`not an object ${obj}`);
  }
  for (let x in obj) {
    return false;
  }
  return true;
}

export function debugEvents(element) {
  for (let key in element) {
    if (/^on/.test(key)) {
      const eventName = key.slice(2);
      element.addEventListener(eventName, (event) =>
        console.log("event triggered:", eventName, event),
      );
    }
  }
}

function levenshteinDistance(a, b) {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let matrix = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j] + 1, // deletion
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

function findClosestMatch(target, strings, threshold) {
  return lo.find(strings, (str) => {
    return levenshteinDistance(target, str) <= threshold;
  });
}

export class ProcessQueue {
  constructor(callback) {
    this._callbackName = callback.name || "anonymous function";
    this._callback = callback;
    this._processing = false;
    this._queue = [];
  }

  processNext(items) {
    /* queues and processes the given items. Note: will resolve immediately if queueing items while processing. */
    if (!Array.isArray(items) || items.length === 0) {
      return Promise.resolve();
    }
    this._queue.push(...items);
    if (!this._processing) {
      return this._triggerProcessing();
    } else {
      return Promise.resolve();
    }
  }

  async _triggerProcessing() {
    this._processing = true;
    try {
      while (this._queue.length > 0) {
        await this.invokeCallback(this._queue.shift());
      }
    } finally {
      this._processing = false;
    }
  }

  async invokeCallback(item) {
    try {
      await this._callback(item);
    } catch (error) {
      console.error(`Error processing ProcessQueue(${this._callbackName})`, {
        callback: this._callback,
        item,
        error,
      });
    }
  }
}
